import React, { SyntheticEvent } from "react";
import { Link as RouterLink } from "react-router-dom";
import PropTypes from "prop-types";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";

import DividerIcon from "../../assets/images/divider-vertical.svg";
import AddIcon from "../../assets/images/add-icon-white.svg";
import EditableTextField from "../../components/EditableTextField";
import AppTabs, { AppTab } from "../../components/AppTabs";
import AddScrambling from "./AddScrambling";
import { Scrambling } from "../../declarations/interface";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: `${theme.spacing(3)} ${theme.spacing(4.5)} ${theme.spacing(1.5)}`,
  },
  heading: {
    fontWeight: 700,
    fontSize: 20,
    letterSpacing: "-4%",
    lineHeight: "24px",
  },
}));

const Header: React.FC<{
  projectName: string;
  projectId: string;
  onProjectNameUpdate: (updatedName: string) => void;
  activeTab: string;
  count: number;
  handleTabChange: (event: SyntheticEvent<Element, Event>, tab: string) => void;
  selectedScrambling?: Scrambling;
  removeSelectedScrambling: () => void;
}> = ({
  projectName,
  projectId,
  onProjectNameUpdate,
  activeTab,
  count,
  handleTabChange,
  selectedScrambling,
  removeSelectedScrambling,
}) => {
  const classes = useStyles();
  return (
    <Box className={classes.root}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2.5}
      >
        <Box>
          <EditableTextField
            value={projectName}
            onSubmit={onProjectNameUpdate}
            className={classes.heading}
          />
        </Box>
        <Box>
          {activeTab === "RECORDINGS" && (
            <Tooltip title={"Create a new recording from existing recordings"}>
              <Button
                startIcon={
                  <img
                    src={AddIcon}
                    alt="create new recording"
                    width="14"
                    height="14"
                  />
                }
                variant="contained"
                color="secondary"
                sx={{
                  padding: "10px 21px",
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.05)",
                  textTransform: "unset !important",
                }}
                to={`/project/${projectId}/new-recording`}
                component={RouterLink}
                aria-label="Create New Recording"
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    lineHeight: "16px",
                  }}
                >
                  Create New Recording
                </Typography>
              </Button>
            </Tooltip>
          )}
          {activeTab === "SCRAMBLING" && (
            <AddScrambling
              scrambling={selectedScrambling}
              projectId={projectId}
              onModalClose={removeSelectedScrambling}
            />
          )}
        </Box>
        {/* search component comes here */}
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <AppTabs value={activeTab} onChange={handleTabChange}>
            <AppTab value="RECORDINGS" label="RECORDINGS" />
            <AppTab disabled className="divider" icon={DividerIcon} />
            <AppTab value="FLOWS" label="FLOWS" />
            <AppTab disabled className="divider" icon={DividerIcon} />
            <AppTab value="SCRAMBLING" label="SCRAMBLING" />
          </AppTabs>
        </Box>
        <Typography
          variant="body1"
          color="primary"
          sx={{ textTransform: "capitalize" }}
        >
          {count} {activeTab.toLowerCase()} found
        </Typography>
      </Box>
    </Box>
  );
};

Header.propTypes = {
  projectName: PropTypes.string.isRequired,
  onProjectNameUpdate: PropTypes.func.isRequired,
  activeTab: PropTypes.oneOf(["RECORDINGS", "FLOWS", "SCRAMBLING"]).isRequired,
  count: PropTypes.number.isRequired,
};

Header.defaultProps = {
  activeTab: "RECORDINGS",
};

export default Header;
